/**
 * Премиальные эмодзи в буфере обмена.
 *
 * ── Что здесь чинится ───────────────────────────────────────────────────────
 *
 * Скопированное из переписки сообщение с премиальными эмодзи при вставке в
 * заготовку (и в композер) теряло их: поле ввода брало из буфера только
 * text/plain, а там на месте эмодзи — запасной символ без идентификатора.
 * В заготовке оставался обычный 😀 вместо премиального.
 *
 * Идентификатор живёт только в HTML-части буфера. Отсюда две половины:
 * при копировании кладём HTML, где каждый премиальный эмодзи — <img> с
 * `data-emoji-id`; при вставке разбираем HTML и достаём идентификаторы.
 * Разбор понимает и то, что кладёт браузер при выделении текста прямо в
 * ленте (наш же <img src=".../custom-emoji/ID">), и формат Bot API
 * (<tg-emoji emoji-id="ID">), и ссылки tg://emoji?id=ID.
 */

import type { TelegramTextEntityDto } from '@tw/shared-types';

export interface RichClipboardValue {
  text: string;
  entities: TelegramTextEntityDto[];
}

/** Идентификатор премиального эмодзи в <img>, <tg-emoji> или <a>. */
function emojiIdOf(el: Element): string | null {
  const direct =
    el.getAttribute('data-emoji-id') ??
    el.getAttribute('data-custom-emoji-id') ??
    el.getAttribute('emoji-id') ??
    el.getAttribute('data-document-id');
  if (direct && /^\d+$/.test(direct)) return direct;

  const src = el.getAttribute('src') ?? '';
  const fromSrc = /\/custom-emoji\/(\d+)(?:[/?#]|$)/.exec(src);
  if (fromSrc) return fromSrc[1] ?? null;

  const href = el.getAttribute('href') ?? '';
  const fromHref = /^tg:\/\/emoji\?id=(\d+)$/.exec(href);
  return fromHref ? (fromHref[1] ?? null) : null;
}

const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'TR', 'BLOCKQUOTE', 'PRE', 'H1', 'H2', 'H3']);

/**
 * Разобрать HTML из буфера. null — премиальных эмодзи там нет, и лучше
 * взять text/plain: он у браузера точнее, чем наш пересказ разметки.
 */
export function parseClipboardHtml(html: string): RichClipboardValue | null {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');

  let text = '';
  const entities: TelegramTextEntityDto[] = [];

  const walk = (node: Node): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent ?? '';
        continue;
      }
      if (!(child instanceof Element)) continue;

      if (child.tagName === 'BR') {
        text += '\n';
        continue;
      }
      if (child.tagName === 'STYLE' || child.tagName === 'SCRIPT') continue;

      const id = emojiIdOf(child);
      if (id) {
        // Символ-заменитель: alt у картинки, текст у tg-emoji/ссылки.
        const emoji =
          (child instanceof HTMLImageElement ? child.alt : child.textContent)?.trim() || '🙂';
        entities.push({ type: 'customEmoji', offset: text.length, length: emoji.length, customEmojiId: id });
        text += emoji;
        continue;
      }

      if (BLOCK_TAGS.has(child.tagName) && text.length > 0 && !text.endsWith('\n')) text += '\n';
      walk(child);
    }
  };
  walk(doc.body);

  if (entities.length === 0) return null;
  return { text: text.replace(/\n$/, ''), entities };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * HTML для буфера: текст с <img data-emoji-id> на месте премиальных эмодзи.
 * Остальная разметка (жирный, ссылки) не переносится — поле ввода её всё
 * равно не понимает, а вставлять в чужие редакторы удобнее без неё.
 */
export function toClipboardHtml(value: RichClipboardValue): string {
  const emojis = value.entities
    .filter((e) => e.type === 'customEmoji' && e.customEmojiId)
    .sort((a, b) => a.offset - b.offset);

  let html = '';
  let cursor = 0;
  for (const e of emojis) {
    if (e.offset < cursor) continue;
    html += escapeHtml(value.text.slice(cursor, e.offset)).replace(/\n/g, '<br>');
    const char = value.text.slice(e.offset, e.offset + e.length);
    const id = e.customEmojiId as string;
    html += `<img src="/api/custom-emoji/${encodeURIComponent(id)}" data-emoji-id="${id}" alt="${escapeHtml(char)}">`;
    cursor = e.offset + e.length;
  }
  html += escapeHtml(value.text.slice(cursor)).replace(/\n/g, '<br>');
  return html;
}

/** Есть ли в разметке премиальные эмодзи — иначе HTML в буфер класть незачем. */
export function hasCustomEmoji(entities: readonly TelegramTextEntityDto[] | undefined): boolean {
  return (entities ?? []).some((e) => e.type === 'customEmoji' && e.customEmojiId);
}

/**
 * Положить в буфер текст вместе с премиальными эмодзи.
 *
 * ClipboardItem с двумя представлениями: text/html для нашего поля ввода
 * и text/plain для всего остального. Отказ (нет разрешения, незащищённый
 * контекст) — false; вызывающий откатывается на writeText.
 */
export async function writeRichClipboard(value: RichClipboardValue): Promise<boolean> {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return false;
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([value.text], { type: 'text/plain' }),
        'text/html': new Blob([toClipboardHtml(value)], { type: 'text/html' }),
      }),
    ]);
    return true;
  } catch {
    return false;
  }
}
